import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedService } from '../shared/services/shared.service';
import { NgClass } from '@angular/common';
import { CollapseModule } from 'ngx-bootstrap/collapse';

export interface SetupTask {
  id: string;
  category: 'required' | 'recommended';
  title: string;
  description: string;
  disabled_functionality: string[];
  functionDetails: string[];
  icon: string;
  route: string;
  queryParams?: Record<string, string>;
  isComplete: boolean;
}

@Component({
  selector: 'app-setup-checklist',
  templateUrl: './setup-checklist.component.html',
  styleUrls: ['./setup-checklist.component.scss'],
  standalone: true,
  imports: [NgClass, CollapseModule],
})
export class SetupChecklistComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  tasks: SetupTask[] = [];
  requiredCollapsed = false;
  recommendedCollapsed = false;
  taskCollapsed: Record<string, boolean> = {};
  isLoading = true;

  get requiredTasks(): SetupTask[] {
    return this.tasks.filter((t) => t.category === 'required');
  }

  get recommendedTasks(): SetupTask[] {
    return this.tasks.filter((t) => t.category === 'recommended');
  }

  get allRequiredComplete(): boolean {
    return this.requiredTasks.length > 0 && this.requiredTasks.every((t) => t.isComplete);
  }

  get completedCount(): number {
    return this.tasks.filter((t) => t.isComplete).length;
  }

  get progressPercent(): number {
    return this.tasks.length ? Math.round((this.completedCount / this.tasks.length) * 100) : 0;
  }

  constructor(
    public sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const fromLogin = this.route.snapshot.queryParamMap.get('fromLogin') === 'true';

    // If company data is already available (e.g. the fetch resolved before this
    // component mounted), render immediately — even for the fromLogin flow.
    if (this.sharedService.company_info_loaded) {
      this.handleSetupLogic(fromLogin);
    }

    // Always listen for the (possibly-still-pending) company_info fetch.
    this.subscription.add(
      this.sharedService.company_info_retrieved$.subscribe((info) => {
        if (info || this.sharedService.company_info) {
          this.handleSetupLogic(fromLogin);
        } else {
          // Prevent an indefinite blank state if no company info is available.
          this.isLoading = false;
        }
      }),
    );

    // Safety net: never leave the page blank for more than 5 seconds.
    // If company_info_retrieved$ never fires (network issue, stale state, etc.),
    // show the checklist with whatever data we have.
    setTimeout(() => {
      if (this.isLoading) {
        console.warn('[SetupChecklist] Safety timeout — forcing isLoading=false');
        this.buildTasks();
        this.isLoading = false;
      }
    }, 5000);
  }

  private handleSetupLogic(fromLogin: boolean): void {
    // If onboarding was dismissed, only skip to dashboard during login flow.
    // Manual navigation (e.g. sidenav) should still allow viewing this page.
    if (fromLogin && this.sharedService.company_info.onboarding_dismissed) {
      this.isLoading = false;          // Always show content in case navigation is delayed
      this.buildTasks();
      this.router.navigate(['/home']);
      return;
    }

    this.buildTasks();

    // Only auto-redirect to dashboard if coming from login and all required tasks are complete
    if (fromLogin && this.allRequiredComplete) {
      this.isLoading = false;          // Always show content in case navigation is delayed
      this.goToDashboard();
      return;
    }

    // Not redirecting, show the page
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private buildTasks(): void {
    const info = this.sharedService.company_info;
    const opts = info?.accounting_options;
    const emailTemplates = info?.email_templates || {};
    const hasEmailTemplate = Object.values(emailTemplates).some(
      (t: any) => t?.subject?.trim() || t?.body?.trim(),
    );



    this.tasks = [
      {
        id: 'live-inventory',
        category: 'required',
        title: 'Select Distributor Accounts',
        description:
          'Select your distributor accounts to enable live inventory pricing and purchase optimization.',
        disabled_functionality: [
          'The Vendor Inventory functions will be unavailable until at least one distributor account is selected.'
        ],
        functionDetails: [
          'Allows users to see available inventory from their distributors.',
          'Allows users to create wants lists and get alerts from their selected distributors.',
          'Allows users to optimize purchasing based on distributor inventory and pricing.',
        ],
        icon: 'fas fa-warehouse',
        route: '/account-dashboard',
        queryParams: { tab: 'tab4' },
        isComplete: (info?.live_inv_selected_distributors?.length ?? 0) > 0,
      },
      {
        id: 'costing-method',
        category: 'required',
        title: 'Set Costing Method for Inventory',
        description:
          'Choose FIFO, LIFO, or Average costing for your inventory. This setting cannot be changed once set.',
        disabled_functionality: [
          'All Accounting features are unavailable until a costing method is selected.'
        ],
        functionDetails: [
          'Updates accounting_options.costing_method_for_inventory in company profile.',
          'Uses Accounting Options tab in Account Dashboard (tab5).',
          'Controls costing behavior in inventory and accounting reporting flows.',
        ],
        icon: 'fas fa-calculator',
        route: '/account-dashboard',
        queryParams: { tab: 'tab5' },
        isComplete: !!opts?.costing_method_for_inventory,
      },
      {
        id: 'sales-tax',
        category: 'required',
        title: 'Configure Sales Tax Rates',
        description:
          'Add the tax rates you collect so they are available when creating sales receipts.',
        functionDetails: [
          'Updates accounting_options.sales_tax_options in company profile.',
          'Uses Accounting Options tab in Account Dashboard (tab5).',
          'Feeds tax selection and calculations in Sales Receipt workflows.',
        ],
        disabled_functionality: [
          'Create Sales receipts functionality is unavailable until sales tax rates are configured.'
        ],
        icon: 'fas fa-percent',
        route: '/account-dashboard',
        queryParams: { tab: 'tab5' },
        isComplete: (opts?.sales_tax_options?.length ?? 0) > 0,
      },
      {
        id: 'form-3310',
        category: 'recommended',
        title: 'ATF Form 3310 Contact Info',
        description:
          "Pre-fill your agency's details to speed up ATF Form 3310 creation on every transfer.",
        functionDetails: [
          'Updates autofill_form_3310 fields in company profile.',
          'Uses Form 3310 Autofill tab in Account Dashboard (tab7).',
          'Pre-populates agency data during Form 3310 generation and email prep.',
        ],
        disabled_functionality: [
          'ATF Form 3310 creation is unavailable until agency contact info is provided.'
        ],
        icon: 'fas fa-id-card',
        route: '/account-dashboard',
        queryParams: { tab: 'tab7' },
        isComplete: !!info?.autofill_form_3310?.agency_name,
      },
      {
        id: 'logo',
        category: 'recommended',
        title: 'Upload Company Logo',
        description: 'Your logo appears on sales receipts, printed documents, and emails.',
        functionDetails: [
          'Stores logo in company_logos collection via company profile APIs.',
          'Uses Account Management tab in Account Dashboard (tab1).',
          'Used by receipt/document rendering and branding display components.',
        ],
        
        icon: 'fas fa-image',
        route: '/account-dashboard',
        queryParams: { tab: 'tab1' },
        isComplete: !!info?.company_logo,
        disabled_functionality: [
          'Company logo is unavailable on Sales receipts and emails until uploaded.'
        ],
      },
      {
        id: 'ffl',
        category: 'recommended',
        title: 'Upload FFL Document',
        description:
          'Attach your Federal Firearms License so it can be included on invoices and transfer paperwork.',
        functionDetails: [
          'Stores FFL doc in company_documents (type=ffl).',
          'Uses Account Management tab in Account Dashboard (tab1).',
          'Used by transfer/FFL email attachments and compliance-related docs.',
        ],
        icon: 'fas fa-certificate',
        route: '/account-dashboard',
        queryParams: { tab: 'tab1' },
        isComplete: !!info?.ffl_document,
        disabled_functionality: [
          'FFL document is unavailable on email requests until uploaded.'
        ],
      },
      {
        id: 'sot',
        category: 'recommended',
        title: 'Upload SOT Document',
        description:
          'Attach your Special Occupational Taxpayer registration if your business deals in NFA items.',
        disabled_functionality: [
          'SOT document is unavailable on templates and document bundles until uploaded.'
        ],
        functionDetails: [
          'Stores SOT doc in company_documents (type=sot).',
          'Uses Account Management tab in Account Dashboard (tab1).',
          'Used when SOT-inclusive templates or document bundles are requested.',
        ],
        icon: 'fas fa-shield-halved',
        route: '/account-dashboard',
        queryParams: { tab: 'tab1' },
        isComplete: !!info?.sot_document,
      },
      {
        id: 'email-templates',
        category: 'recommended',
        title: 'Configure Email Templates',
        description:
          'Customize the emails sent for FFL requests, firearm transfers, and ATF Form 3310.',
        disabled_functionality: [
          'Email templates are unavailable for selection in email flows until at least one template is configured.'
        ],
        functionDetails: [
          'Updates email_templates map on company profile.',
          'Uses Email Config page (/email-config).',
          'Controls default subject/body and include_ffl/include_sot behavior in send-email flows.',
        ],
        icon: 'fas fa-envelope-open-text',
        route: '/email-config',
        isComplete: hasEmailTemplate,
      },
    ];

    for (const task of this.tasks) {
      if (!(task.id in this.taskCollapsed)) {
        this.taskCollapsed[task.id] = true;
      }
    }
  }

  goToTask(task: SetupTask): void {
    if (task.queryParams) {
      this.router.navigate([task.route], { queryParams: task.queryParams });
    } else {
      this.router.navigate([task.route]);
    }
  }

  toggleRequired(): void {
    this.requiredCollapsed = !this.requiredCollapsed;
  }

  toggleRecommended(): void {
    this.recommendedCollapsed = !this.recommendedCollapsed;
  }

  toggleTask(taskId: string): void {
    this.taskCollapsed[taskId] = !this.taskCollapsed[taskId];
  }

  isTaskCollapsed(taskId: string): boolean {
    return this.taskCollapsed[taskId] ?? true;
  }

  async goToDashboard(): Promise<void> {
    await this.sharedService.dismissOnboarding();
    this.router.navigate(['/home']);
  }
}
